import { useCallback, useContext, useEffect, useMemo, useState } from "react";

//types
import { DELIVERY_SECTION_ELEMENT, useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";
import { RenderingDetails, ReviewedCartApiResponse, SingleTabMeta } from "@swiggy-private/cart-types";
import { ITabBarItem } from "@swiggy-private/dls-web-core/dist/components/TabContainer/components/TabBar/types";

//constants
import { VALID_DELIVERY_RENDERING_ITEMS } from "@cart-client/constants/CartDeliverySpeed";

//contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";

//utils
import { isEmpty } from "@swiggy-private/js-utils";
import { getDeliverySpeedWidgetInfoV2 } from "@cart-client/helpers/CartDeliverySpeed";
import { getDeliveryInstructionData } from "@cart-client/helpers/Cart";
import { filterRenderingDetails } from "@cart-client/helpers/CartRendering";
import { getDeliverySectionA11yLabel, getDeliveryTabLabel } from "@cart-client/helpers/DeliveryRenderer";
import { getMROChildCarts, isMROFlow } from "@cart-client/helpers/MROFoodCart";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

//hooks
import { useDeliveryRenderedAnalytics } from "./useDeliveryRenderedAnalytics";
import { LOCAL_STORAGE_KEYS } from "@swiggy-private/core-ui";
import { getDeliveryTabRightIcon } from "@cart-client/helpers/DeliveryRendererIcons";

export const useDeliveryRenderer = (renderingDetails: ReviewedCartApiResponse["rendering_details"]) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const { selectedOption, onTabChange: onTabOptionChange, setDeliveryTab } = useContext(DeliverySectionContext);
    const {
        cartState: { localCart, reviewedCart: cartResponse },
    } = useContext(CartContext);

    const { onTabToggleClick, onTabToggleImpression } = useDeliveryRenderedAnalytics(cartResponse);

    const mroCanShowDeliverySpeed = useMemo(() => {
        if (!isMROFlow()) return false;
        const childCarts = getMROChildCarts(cartResponse as MROReviewedCart);
        return (
            !isEmpty(childCarts) &&
            childCarts.every((child) => !isEmpty(getDeliverySpeedWidgetInfoV2(child, localCart)))
        );
    }, [cartResponse, localCart]);

    const { canShowTip, canShowInstructions, canShowDeliverySpeed } = useMemo(() => {
        const isMROCart = isMROFlow();
        return {
            canShowTip: !isEmpty(cartResponse?.data?.tipExperimentData) || !isEmpty(cartResponse?.data?.tipDetails),
            canShowInstructions: !isEmpty(getDeliveryInstructionData(cartResponse)),
            canShowDeliverySpeed: isMROCart
                ? mroCanShowDeliverySpeed
                : !isEmpty(getDeliverySpeedWidgetInfoV2(cartResponse?.data, localCart)),
        };
    }, [cartResponse, localCart, mroCanShowDeliverySpeed]);

    const newTagMaxFreqCount = useMemo(
        () => cartResponse?.data?.toing_presentational_config?.delivery_modes_ftue_data?.new_tag_max_count,
        [cartResponse],
    );

    const { shouldProceed: shouldShowDeliverySpeedNewTag } = useLocalStorageBasedFrequencyCapping({
        key: LOCAL_STORAGE_KEYS.DELIVERY_SPEED_NEW_TAG_FREQUENCY_CAPPING_KEY,
        maxFrequency: newTagMaxFreqCount,
    });

    const canRenderTab = useCallback(
        (tab: DELIVERY_SECTION_ELEMENT): boolean => {
            switch (tab?.toUpperCase()) {
                case DELIVERY_SECTION_ELEMENT.TIP:
                    return canShowTip;
                case DELIVERY_SECTION_ELEMENT.INSTRUCTIONS:
                    return canShowInstructions;
                case DELIVERY_SECTION_ELEMENT.DELIVERY_TYPE:
                    return canShowDeliverySpeed;
                case DELIVERY_SECTION_ELEMENT.SELF_DELIVERY:
                    return true;
                default:
                    return false;
            }
        },
        [canShowDeliverySpeed, canShowTip, canShowInstructions],
    );

    const onTabChange = useCallback(
        (tab: DELIVERY_SECTION_ELEMENT) => {
            onTabOptionChange(tab);
            onTabToggleClick(tab);
        },
        [onTabOptionChange, onTabToggleClick],
    );

    const deliveryRenderingDetails = useMemo(
        () => filterRenderingDetails(renderingDetails, VALID_DELIVERY_RENDERING_ITEMS),
        [renderingDetails],
    );

    const tabOptions: ITabBarItem[] = useMemo(
        () =>
            deliveryRenderingDetails
                .filter((renderDetail) => canRenderTab(renderDetail?.type as DELIVERY_SECTION_ELEMENT))
                .map((renderDetail) => {
                    const tabType = renderDetail?.type?.toUpperCase() as DELIVERY_SECTION_ELEMENT;
                    return {
                        id: tabType,
                        label: getDeliveryTabLabel(tabType),
                        isSelected: selectedOption === tabType,
                        meta: renderDetail,
                        ariaLabel: getDeliverySectionA11yLabel(renderDetail, selectedOption),
                        rightIcon: {
                            customNode: shouldShowDeliverySpeedNewTag() ? getDeliveryTabRightIcon(tabType) : null,
                        },
                    };
                }),
        [deliveryRenderingDetails, canRenderTab, selectedOption, shouldShowDeliverySpeedNewTag],
    );
    const singleTabMeta = useMemo((): SingleTabMeta => {
        const singleTabDetail = tabOptions.length === 1 ? (tabOptions[0]?.meta as RenderingDetails) : undefined;
        return {
            isCollapsible: !!singleTabDetail?.meta?.is_collapsible,
            icon: singleTabDetail?.icon,
            title: singleTabDetail?.display_text?.trim(),
        };
    }, [tabOptions]);

    useEffect(() => {
        if (isEmpty(tabOptions)) {
            if (isEmpty(selectedOption)) {
                setError(true);
                setLoading(false);
            }
            return;
        }

        const isSelectedTabAvailable = !isEmpty(selectedOption) && tabOptions.some((tab) => tab.id === selectedOption);
        // go to first tab
        if (isEmpty(selectedOption) || (isMROFlow() && !isSelectedTabAvailable)) {
            setDeliveryTab(tabOptions[0].id as DELIVERY_SECTION_ELEMENT, false);
            onTabToggleImpression(tabOptions[0].id);
        }

        setLoading(false);
    }, [selectedOption, setDeliveryTab, tabOptions, onTabToggleImpression]);

    return {
        onChange: onTabChange,
        selectedOption,
        tabOptions,
        loading,
        error,
        singleTabMeta,
    };
};
